/**
 * Component Schema Registry
 *
 * Comprehensive schemas for all component types.
 * Supports both manually maintained schemas (below) and auto-generated schemas.
 *
 * Schema Priority:
 * 1. Manual overrides (this file) - for custom field behavior
 * 2. Generated schemas (componentSchemas.generated.ts) - auto-synced from TypeScript
 *
 * To regenerate schemas: npm run sync-schemas
 */

// Generated schemas - imported if available
// These will be populated by loadGeneratedSchemas() at runtime
let generatedComponentSchemas: Record<string, ComponentSchema> = {}
let generatedArrayItemSchemas: Record<string, Record<string, PropertyDefinition>> = {}
let generatedArrayMappings: Record<string, Record<string, string>> = {}
let generatedSchemasLoaded = false

/**
 * Lazy-load generated schemas on first access
 * This allows the module to work even if generated file doesn't exist
 */
async function loadGeneratedSchemas(): Promise<void> {
  if (generatedSchemasLoaded) return

  try {
    const generated = await import('./componentSchemas.generated')
    generatedComponentSchemas = generated.generatedComponentSchemas || {}
    generatedArrayItemSchemas = generated.generatedArrayItemSchemas || {}
    generatedArrayMappings = generated.generatedArrayMappings || {}
  } catch {
    // Generated schemas don't exist yet - will be created by npm run sync-schemas
  }

  generatedSchemasLoaded = true
}

// Initialize generated schemas immediately (fire and forget)
loadGeneratedSchemas()

export interface PropertyDefinition {
  type: 'string' | 'textarea' | 'select' | 'color' | 'array' | 'boolean' | 'number' | 'url' | 'image' | 'stringList'
  category: 'content' | 'appearance' | 'layout' | 'advanced'
  label: string
  optional?: boolean
  options?: string[]
  description?: string
  /**
   * The stored value is HTML, but the component renders it as simple paragraphs.
   * The editor edits it as PLAIN TEXT (strip tags on load, re-wrap on save) so the
   * owner never sees markup (D1). Only set this for fields whose HTML is paragraph-
   * level; fields needing rich/structured markup must NOT set it.
   */
  html?: boolean
  /**
   * For `array` fields INSIDE an array item (nested arrays, e.g. menu category → items):
   * the `arrayItemSchemas` key describing each nested item. Top-level component arrays
   * resolve their item schema via `getArrayItemSchema` mappings instead.
   */
  itemSchema?: string
}

export interface ComponentSchema {
  properties: Record<string, PropertyDefinition>
}

/**
 * Complete component schema registry for all 13 component types
 */
export const componentSchemas: Record<string, ComponentSchema> = {
  // Banner Section (v1)
  'cms.banner-section': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title' },
      displayTitle: { type: 'string', category: 'content', label: 'Display Title', optional: true },
      description: { type: 'textarea', category: 'content', label: 'Description', optional: true },
      subtitle: { type: 'string', category: 'content', label: 'Subtitle', optional: true },
      shortCTA: { type: 'string', category: 'content', label: 'Short CTA', optional: true },
      fullCTA: { type: 'string', category: 'content', label: 'Full CTA', optional: true },
      CTALink: { type: 'url', category: 'content', label: 'CTA Link', optional: true },
      variant: {
        type: 'select',
        category: 'appearance',
        label: 'Variant',
        options: ['hero', 'cta', 'banner', 'announcement', 'overlay-box-bottom', 'overlay-box-right', 'fullscreen-nav', 'background-image'],
        optional: true
      },
      textPosition: {
        type: 'select',
        category: 'layout',
        label: 'Text Position',
        options: ['center', 'left', 'right', 'bottom-box', 'right-box'],
        optional: true
      },
      size: {
        type: 'select',
        category: 'appearance',
        label: 'Size',
        options: ['small', 'medium', 'large', 'extra-large', 'professional', 'dramatic'],
        optional: true
      },
      density: {
        type: 'select',
        category: 'layout',
        label: 'Density',
        options: ['compact', 'normal', 'loose'],
        optional: true
      },
      backgroundType: {
        type: 'select',
        category: 'appearance',
        label: 'Background Type',
        options: ['solid', 'gradient', 'image'],
        optional: true
      },
      backgroundColor: { type: 'color', category: 'appearance', label: 'Background Color', optional: true },
      backgroundGradient: { type: 'string', category: 'appearance', label: 'Background Gradient', optional: true },
      overlayOpacity: { type: 'number', category: 'appearance', label: 'Overlay Opacity', optional: true },
      buttonStyle: {
        type: 'select',
        category: 'appearance',
        label: 'Button Style',
        options: ['primary', 'secondary', 'outline'],
        optional: true
      },
      Image: { type: 'image', category: 'content', label: 'Background Image', optional: true }
    }
  },

  // Banner Section V2
  'cms.banner-section-v2': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title' },
      displayTitle: { type: 'string', category: 'content', label: 'Display Title', optional: true },
      description: { type: 'textarea', category: 'content', label: 'Description', optional: true },
      subtitle: { type: 'string', category: 'content', label: 'Subtitle', optional: true },
      shortCTA: { type: 'string', category: 'content', label: 'Short CTA', optional: true },
      fullCTA: { type: 'string', category: 'content', label: 'Full CTA', optional: true },
      CTALink: { type: 'url', category: 'content', label: 'CTA Link', optional: true },
      variant: {
        type: 'select',
        category: 'appearance',
        label: 'Variant',
        options: ['hero', 'cta', 'banner', 'overlay'],
        optional: true
      },
      textPosition: {
        type: 'select',
        category: 'layout',
        label: 'Text Position',
        options: ['center', 'left', 'right'],
        optional: true
      },
      size: {
        type: 'select',
        category: 'appearance',
        label: 'Size',
        options: ['small', 'medium', 'large', 'extra-large', 'professional', 'dramatic'],
        optional: true
      },
      density: {
        type: 'select',
        category: 'layout',
        label: 'Density',
        options: ['compact', 'normal', 'loose'],
        optional: true
      },
      backgroundType: {
        type: 'select',
        category: 'appearance',
        label: 'Background Type',
        options: ['solid', 'gradient', 'image'],
        optional: true
      },
      backgroundColor: { type: 'color', category: 'appearance', label: 'Background Color', optional: true },
      backgroundGradient: { type: 'string', category: 'appearance', label: 'Background Gradient', optional: true },
      overlayOpacity: { type: 'number', category: 'appearance', label: 'Overlay Opacity', optional: true },
      buttonStyle: {
        type: 'select',
        category: 'appearance',
        label: 'Button Style',
        options: ['primary', 'secondary', 'outline'],
        optional: true
      },
      Image: { type: 'image', category: 'content', label: 'Background Image', optional: true }
    }
  },

  // Announcement Banner
  'cms.announcement-banner': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title' },
      linkText: { type: 'string', category: 'content', label: 'Link Text', optional: true },
      link: { type: 'url', category: 'content', label: 'Link URL', optional: true },
      backgroundColor: { type: 'color', category: 'appearance', label: 'Background Color', optional: true },
      textColor: { type: 'color', category: 'appearance', label: 'Text Color', optional: true }
    }
  },

  // Text Section
  'cms.text-section': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title', optional: true },
      subtitle: { type: 'string', category: 'content', label: 'Subtitle', optional: true },
      // Stored as HTML, edited as plain text in the inspector (D1): owners see
      // paragraphs, not <p> markup. TextSection renders simple paragraph HTML.
      content: { type: 'textarea', category: 'content', label: 'Content', html: true },
      layout: {
        type: 'select',
        category: 'layout',
        label: 'Layout',
        options: ['flow', 'highlight', 'structured', 'compact'],
        optional: true
      },
      density: {
        type: 'select',
        category: 'layout',
        label: 'Density',
        options: ['tight', 'normal', 'loose'],
        optional: true
      },
      emphasis: {
        type: 'select',
        category: 'appearance',
        label: 'Emphasis',
        options: ['subtle', 'normal', 'strong'],
        optional: true
      },
      hierarchy: {
        type: 'select',
        category: 'appearance',
        label: 'Hierarchy',
        options: ['flat', 'structured', 'dramatic'],
        optional: true
      },
      imagePosition: {
        type: 'select',
        category: 'layout',
        label: 'Image Position',
        options: ['left', 'right', 'top'],
        optional: true
      },
      imageSize: {
        type: 'select',
        category: 'appearance',
        label: 'Image Size',
        options: ['small', 'medium', 'large'],
        optional: true
      },
      textAlign: {
        type: 'select',
        category: 'layout',
        label: 'Text Alignment',
        options: ['left', 'center', 'right', 'justify'],
        optional: true
      },
      showDivider: { type: 'boolean', category: 'appearance', label: 'Show Divider', optional: true },
      equalHeight: { type: 'boolean', category: 'layout', label: 'Equal Height', optional: true },
      Image: { type: 'image', category: 'content', label: 'Image', optional: true }
    }
  },

  // Card Section
  'cms.card-section': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title', optional: true },
      layout: {
        type: 'select',
        category: 'layout',
        label: 'Layout',
        options: ['standard', 'compact', 'overlay', 'stats'],
        optional: true
      },
      emphasis: {
        type: 'select',
        category: 'appearance',
        label: 'Emphasis',
        options: ['subtle', 'normal', 'strong'],
        optional: true
      },
      alignment: {
        type: 'select',
        category: 'layout',
        label: 'Alignment',
        options: ['left', 'center', 'justified'],
        optional: true
      },
      cardStyle: {
        type: 'select',
        category: 'appearance',
        label: 'Card Style',
        options: ['elevated', 'outlined', 'flat', 'gradient'],
        optional: true
      },
      size: {
        type: 'select',
        category: 'appearance',
        label: 'Size',
        options: ['small', 'medium', 'large', 'extra-large', 'professional', 'dramatic'],
        optional: true
      },
      imagePosition: {
        type: 'select',
        category: 'layout',
        label: 'Image Position',
        options: ['top', 'left', 'right', 'background'],
        optional: true
      },
      imageAspectRatio: {
        type: 'select',
        category: 'appearance',
        label: 'Image Aspect Ratio',
        options: ['square', 'landscape', 'portrait', 'auto'],
        optional: true
      },
      showButtons: { type: 'boolean', category: 'appearance', label: 'Show Buttons', optional: true },
      buttonText: { type: 'string', category: 'content', label: 'Button Text', optional: true },
      buttonStyle: {
        type: 'select',
        category: 'appearance',
        label: 'Button Style',
        options: ['primary', 'secondary', 'text', 'outline'],
        optional: true
      },
      equalHeight: { type: 'boolean', category: 'layout', label: 'Equal Height', optional: true }
    }
  },

  // Contact Form
  'cms.contact-form': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title', optional: true },
      description: { type: 'textarea', category: 'content', label: 'Description', optional: true },
      submitText: { type: 'string', category: 'content', label: 'Submit Button Text', optional: true },
      successMessage: { type: 'textarea', category: 'content', label: 'Success Message', optional: true },
      nameLabel: { type: 'string', category: 'content', label: 'Name Field Label', optional: true },
      emailLabel: { type: 'string', category: 'content', label: 'Email Field Label', optional: true },
      messageLabel: { type: 'string', category: 'content', label: 'Message Field Label', optional: true },
      namePlaceholder: { type: 'string', category: 'content', label: 'Name Placeholder', optional: true },
      emailPlaceholder: { type: 'string', category: 'content', label: 'Email Placeholder', optional: true },
      messagePlaceholder: { type: 'string', category: 'content', label: 'Message Placeholder', optional: true }
    }
  },

  // Flex Section
  'cms.flex-section': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title', optional: true },
      layout: {
        type: 'select',
        category: 'layout',
        label: 'Layout',
        options: ['grid', 'list', 'masonry', 'gallery-strip', 'flex-row', 'flex-col'],
        optional: true
      },
      columns: { type: 'number', category: 'layout', label: 'Columns', optional: true },
      gap: {
        type: 'select',
        category: 'layout',
        label: 'Gap',
        options: ['none', 'small', 'medium', 'large'],
        optional: true
      },
      align: {
        type: 'select',
        category: 'layout',
        label: 'Align',
        options: ['start', 'center', 'end', 'stretch'],
        optional: true
      },
      justify: {
        type: 'select',
        category: 'layout',
        label: 'Justify',
        options: ['start', 'center', 'end', 'between', 'around'],
        optional: true
      },
      wrap: { type: 'boolean', category: 'layout', label: 'Wrap', optional: true },
      equalHeight: { type: 'boolean', category: 'layout', label: 'Equal Height', optional: true },
      containerWidth: {
        type: 'select',
        category: 'layout',
        label: 'Container Width',
        options: ['contained', 'wide', 'full'],
        optional: true
      },
      Items: { type: 'array', category: 'content', label: 'Items' }
    }
  },

  // Image Gallery
  'cms.image-gallery': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title', optional: true },
      variant: {
        type: 'select',
        category: 'appearance',
        label: 'Variant',
        options: ['carousel'],
        optional: true
      },
      showCaptions: { type: 'boolean', category: 'appearance', label: 'Show Captions', optional: true },
      enableLightbox: { type: 'boolean', category: 'appearance', label: 'Enable Lightbox', optional: true },
      numVisible: { type: 'number', category: 'layout', label: 'Number Visible', optional: true },
      numScroll: { type: 'number', category: 'layout', label: 'Number to Scroll', optional: true },
      autoPlay: { type: 'boolean', category: 'layout', label: 'Auto Play', optional: true },
      autoPlayInterval: { type: 'number', category: 'layout', label: 'Auto Play Interval (ms)', optional: true },
      images: { type: 'array', category: 'content', label: 'Images' }
    }
  },

  // Thank You Section
  'cms.thank-you': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title' },
      message: { type: 'textarea', category: 'content', label: 'Message' },
      homeLink: { type: 'url', category: 'content', label: 'Home Link', optional: true }
    }
  },

  // Accordion Section (v1)
  'cms.accordion-section': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title', optional: true },
      subtitle: { type: 'string', category: 'content', label: 'Subtitle', optional: true },
      variant: {
        type: 'select',
        category: 'appearance',
        label: 'Variant',
        options: ['default', 'minimal'],
        optional: true
      },
      size: {
        type: 'select',
        category: 'appearance',
        label: 'Size',
        options: ['small', 'medium', 'large', 'extra-large', 'professional', 'dramatic'],
        optional: true
      },
      density: {
        type: 'select',
        category: 'layout',
        label: 'Density',
        options: ['compact', 'normal', 'loose'],
        optional: true
      },
      allowMultiple: { type: 'boolean', category: 'layout', label: 'Allow Multiple Open', optional: true },
      columns: { type: 'number', category: 'layout', label: 'Columns', optional: true },
      Items: { type: 'array', category: 'content', label: 'Accordion Items' }
    }
  },

  // Accordion Section V2
  'cms.accordion-section-v2': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title', optional: true },
      subtitle: { type: 'string', category: 'content', label: 'Subtitle', optional: true },
      style: {
        type: 'select',
        category: 'appearance',
        label: 'Style',
        options: ['default', 'compact'],
        optional: true
      },
      allowMultiple: { type: 'boolean', category: 'layout', label: 'Allow Multiple Open', optional: true },
      Items: { type: 'array', category: 'content', label: 'Accordion Items' }
    }
  },

  // Accordion V3 (new naming convention)
  'cms.accordion-v3': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title', optional: true },
      subtitle: { type: 'string', category: 'content', label: 'Subtitle', optional: true },
      style: {
        type: 'select',
        category: 'appearance',
        label: 'Style',
        options: ['default', 'compact'],
        optional: true
      },
      allowMultiple: { type: 'boolean', category: 'layout', label: 'Allow Multiple Open', optional: true },
      Items: { type: 'array', category: 'content', label: 'Accordion Items' }
    }
  },

  // Gallery Nav
  'cms.gallery-nav': {
    properties: {
      filters: { type: 'array', category: 'content', label: 'Filter Options' }
    }
  },

  // Banner V4 (current)
  'cms.banner-v4': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title' },
      displayTitle: { type: 'string', category: 'content', label: 'Display Title', optional: true },
      description: { type: 'textarea', category: 'content', label: 'Description', optional: true },
      subtitle: { type: 'string', category: 'content', label: 'Subtitle', optional: true },
      shortCTA: { type: 'string', category: 'content', label: 'Short CTA', optional: true },
      fullCTA: { type: 'string', category: 'content', label: 'Full CTA', optional: true },
      CTALink: { type: 'url', category: 'content', label: 'CTA Link', optional: true },
      size: {
        type: 'select',
        category: 'appearance',
        label: 'Size',
        options: ['small', 'medium', 'large', 'dramatic']
      },
      variant: {
        type: 'select',
        category: 'appearance',
        label: 'Variant',
        options: ['hero', 'cta', 'banner'],
        optional: true
      },
      backgroundType: {
        type: 'select',
        category: 'appearance',
        label: 'Background Type',
        options: ['solid', 'gradient', 'image'],
        optional: true
      },
      backgroundColor: { type: 'color', category: 'appearance', label: 'Background Color', optional: true },
      backgroundGradient: { type: 'string', category: 'appearance', label: 'Background Gradient', optional: true },
      Image: { type: 'image', category: 'appearance', label: 'Background Image', optional: true },
      overlayOpacity: { type: 'number', category: 'appearance', label: 'Overlay Opacity', optional: true },
      buttonStyle: {
        type: 'select',
        category: 'appearance',
        label: 'Button Style',
        options: ['primary', 'secondary', 'outline'],
        optional: true
      },
      textPosition: {
        type: 'select',
        category: 'layout',
        label: 'Text Position',
        options: ['center', 'left', 'right'],
        optional: true
      }
    }
  },

  // LayoutGrid - Bootstrap-based grid layout
  'cms.layout-grid': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title', optional: true },
      layout: {
        type: 'select',
        category: 'layout',
        label: 'Layout',
        options: ['2-col', '3-col', '4-col', '2-col-sidebar', 'masonry']
      },
      gap: {
        type: 'select',
        category: 'layout',
        label: 'Gap',
        options: ['none', 'small', 'medium', 'large'],
        optional: true
      },
      containerWidth: {
        type: 'select',
        category: 'layout',
        label: 'Container Width',
        options: ['contained', 'wide', 'full'],
        optional: true
      },
      equalHeight: { type: 'boolean', category: 'layout', label: 'Equal Height', optional: true },
      Items: { type: 'array', category: 'content', label: 'Grid Items' }
    }
  },

  // ── Diversity-roadmap components (hand-written: the generated entries carry placeholder
  // {title,value} items, and pricing is generated under the wrong key) ──────────────

  // Process Steps
  'cms.process-steps': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title', optional: true },
      subtitle: { type: 'string', category: 'content', label: 'Subtitle', optional: true },
      Items: { type: 'array', category: 'content', label: 'Steps' },
      style: {
        type: 'select',
        category: 'appearance',
        label: 'Style',
        options: ['plain', 'connected', 'numbered'],
        optional: true
      },
      orientation: {
        type: 'select',
        category: 'layout',
        label: 'Orientation',
        options: ['horizontal', 'vertical'],
        optional: true
      },
      spacing: {
        type: 'select',
        category: 'layout',
        label: 'Spacing',
        options: ['compact', 'normal', 'airy', 'spacious'],
        optional: true
      }
    }
  },

  // Location Block
  'cms.location-block': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title', optional: true },
      address: { type: 'textarea', category: 'content', label: 'Address' },
      phone: { type: 'string', category: 'content', label: 'Phone', optional: true },
      email: { type: 'string', category: 'content', label: 'Email', optional: true },
      hours: { type: 'array', category: 'content', label: 'Opening Hours', optional: true },
      mapEmbedUrl: { type: 'url', category: 'content', label: 'Map Embed URL', optional: true },
      staticMapUrl: { type: 'url', category: 'content', label: 'Static Map Image URL', optional: true },
      layout: {
        type: 'select',
        category: 'layout',
        label: 'Layout',
        options: ['map-left', 'map-right', 'map-full'],
        optional: true
      },
      spacing: {
        type: 'select',
        category: 'layout',
        label: 'Spacing',
        options: ['compact', 'normal', 'airy', 'spacious'],
        optional: true
      }
    }
  },

  // Quote
  'cms.quote': {
    properties: {
      quote: { type: 'textarea', category: 'content', label: 'Quote' },
      author: { type: 'string', category: 'content', label: 'Author', optional: true },
      role: { type: 'string', category: 'content', label: 'Role', optional: true },
      company: { type: 'string', category: 'content', label: 'Company', optional: true },
      image: { type: 'image', category: 'content', label: 'Image', optional: true },
      variant: {
        type: 'select',
        category: 'appearance',
        label: 'Variant',
        options: ['centered', 'side-image', 'large'],
        optional: true
      },
      spacing: {
        type: 'select',
        category: 'layout',
        label: 'Spacing',
        options: ['compact', 'normal', 'airy', 'spacious'],
        optional: true
      }
    }
  },

  // Logo Strip
  'cms.logo-strip': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title', optional: true },
      logos: { type: 'array', category: 'content', label: 'Logos' },
      variant: {
        type: 'select',
        category: 'appearance',
        label: 'Variant',
        options: ['static', 'marquee'],
        optional: true
      },
      grayscale: { type: 'boolean', category: 'appearance', label: 'Grayscale', optional: true },
      spacing: {
        type: 'select',
        category: 'layout',
        label: 'Spacing',
        options: ['compact', 'normal', 'airy', 'spacious'],
        optional: true
      }
    }
  },

  // Pricing (type is 'cms.pricing' — the generated file keys it 'cms.pricing-table')
  'cms.pricing': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title', optional: true },
      subtitle: { type: 'string', category: 'content', label: 'Subtitle', optional: true },
      billingNote: { type: 'string', category: 'content', label: 'Billing Note', optional: true },
      Items: { type: 'array', category: 'content', label: 'Pricing Tiers' },
      layout: {
        type: 'select',
        category: 'layout',
        label: 'Layout',
        options: ['2-col', '3-col'],
        optional: true
      },
      spacing: {
        type: 'select',
        category: 'layout',
        label: 'Spacing',
        options: ['compact', 'normal', 'airy', 'spacious'],
        optional: true
      }
    }
  },

  // Menu List
  'cms.menu-list': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title', optional: true },
      subtitle: { type: 'string', category: 'content', label: 'Subtitle', optional: true },
      categories: { type: 'array', category: 'content', label: 'Categories' },
      layout: {
        type: 'select',
        category: 'layout',
        label: 'Layout',
        options: ['single', 'two-col'],
        optional: true
      },
      spacing: {
        type: 'select',
        category: 'layout',
        label: 'Spacing',
        options: ['compact', 'normal', 'airy', 'spacious'],
        optional: true
      }
    }
  },

  // Feature Rows
  'cms.feature-rows': {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title', optional: true },
      subtitle: { type: 'string', category: 'content', label: 'Subtitle', optional: true },
      Items: { type: 'array', category: 'content', label: 'Rows' },
      spacing: {
        type: 'select',
        category: 'layout',
        label: 'Spacing',
        options: ['compact', 'normal', 'airy', 'spacious'],
        optional: true
      }
    }
  }
}

/**
 * Get schema for a component type with fallback
 *
 * Priority:
 * 1. Manual schema (componentSchemas)
 * 2. Generated schema (generatedComponentSchemas)
 * 3. Default fallback
 */
export function getComponentSchema(componentType: string): ComponentSchema {
  // Manual override takes priority
  if (componentSchemas[componentType]) {
    return componentSchemas[componentType]
  }

  // Try generated schema
  if (generatedComponentSchemas[componentType]) {
    return generatedComponentSchemas[componentType]
  }

  // Default fallback
  return {
    properties: {
      title: { type: 'string', category: 'content', label: 'Title', optional: true },
      description: { type: 'textarea', category: 'content', label: 'Description', optional: true }
    }
  }
}

/**
 * Get properties by category for a component
 */
export function getPropertiesByCategory(componentType: string, category: string): [string, PropertyDefinition][] {
  const schema = getComponentSchema(componentType)
  return Object.entries(schema.properties).filter(([, def]) => def.category === category)
}

/**
 * Check if a component has a specific property
 */
export function hasProperty(componentType: string, property: string): boolean {
  const schema = getComponentSchema(componentType)
  return property in schema.properties
}

/**
 * Array item schemas for components that contain arrays
 */
export const arrayItemSchemas: Record<string, Record<string, PropertyDefinition>> = {
  // AccordionItem schema (used by accordion-section, accordion-section-v2, accordion-v3)
  AccordionItem: {
    title: { type: 'string', category: 'content', label: 'Title' },
    content: { type: 'textarea', category: 'content', label: 'Content', html: true },
    icon: { type: 'string', category: 'appearance', label: 'Icon', optional: true },
    expanded: { type: 'boolean', category: 'layout', label: 'Expanded by default', optional: true }
  },

  // FlexItem schema (used by flex-section) - simplified for now
  FlexItem: {
    width: {
      type: 'select',
      category: 'layout',
      label: 'Width',
      options: ['auto', 'full', '1/2', '1/3', '1/4', '2/3', '3/4'],
      optional: true
    },
    order: { type: 'number', category: 'layout', label: 'Order', optional: true },
    masonrySize: {
      type: 'select',
      category: 'layout',
      label: 'Masonry Size',
      options: ['small', 'normal', 'large', 'wide', 'wide-tall', 'featured'],
      optional: true
    }
    // Note: FlexItem.content is a nested Section - complex editing will be Phase 3
  },

  // ImageGalleryItem schema (used by image-gallery)
  // `title` is what ImageGallery.vue renders as the image alt AND caption heading.
  ImageGalleryItem: {
    url: { type: 'url', category: 'content', label: 'Image URL' },
    title: { type: 'string', category: 'content', label: 'Title / alt text', optional: true },
    caption: { type: 'string', category: 'content', label: 'Caption', optional: true }
  },

  // GalleryNavFilter schema (used by gallery-nav)
  GalleryNavFilter: {
    label: { type: 'string', category: 'content', label: 'Filter Label' },
    value: { type: 'string', category: 'content', label: 'Filter Value' },
    active: { type: 'boolean', category: 'layout', label: 'Active by default', optional: true }
  },

  // LayoutGridItem schema (used by layout-grid)
  LayoutGridItem: {
    colSpan: {
      type: 'select',
      category: 'layout',
      label: 'Column Span',
      options: ['1', '2', '3', '4', '6', '12'],
      optional: true
    },
    order: { type: 'number', category: 'layout', label: 'Order', optional: true }
    // Note: content is nested Section - complex editing handled separately
  },

  // ProcessStepItem (cms.process-steps Items)
  ProcessStepItem: {
    title: { type: 'string', category: 'content', label: 'Title' },
    description: { type: 'textarea', category: 'content', label: 'Description', optional: true },
    icon: { type: 'string', category: 'appearance', label: 'Icon', optional: true }
  },

  // OpeningHour (cms.location-block hours)
  OpeningHour: {
    day: { type: 'string', category: 'content', label: 'Day' },
    hours: { type: 'string', category: 'content', label: 'Hours' }
  },

  // Logo (cms.logo-strip logos are ImageAssets)
  LogoItem: {
    url: { type: 'url', category: 'content', label: 'Logo URL' },
    alternativeText: { type: 'string', category: 'content', label: 'Alt text (company name)', optional: true }
  },

  // PricingTier (cms.pricing Items)
  PricingTier: {
    name: { type: 'string', category: 'content', label: 'Name' },
    price: { type: 'string', category: 'content', label: 'Price' },
    period: { type: 'string', category: 'content', label: 'Period (e.g. per month)', optional: true },
    description: { type: 'textarea', category: 'content', label: 'Description', optional: true },
    features: { type: 'stringList', category: 'content', label: 'Features (one per line)' },
    ctaText: { type: 'string', category: 'content', label: 'Button Text', optional: true },
    ctaLink: { type: 'url', category: 'content', label: 'Button Link', optional: true },
    badge: { type: 'string', category: 'appearance', label: 'Badge', optional: true },
    highlighted: { type: 'boolean', category: 'appearance', label: 'Highlighted', optional: true }
  },

  // MenuCategory (cms.menu-list categories) → nested MenuListItem
  MenuCategory: {
    name: { type: 'string', category: 'content', label: 'Category Name' },
    items: { type: 'array', category: 'content', label: 'Items', itemSchema: 'MenuListItem' }
  },

  // MenuListItem (nested in MenuCategory.items)
  MenuListItem: {
    name: { type: 'string', category: 'content', label: 'Name' },
    description: { type: 'textarea', category: 'content', label: 'Description', optional: true },
    price: { type: 'string', category: 'content', label: 'Price', optional: true },
    tags: { type: 'stringList', category: 'content', label: 'Tags (one per line)', optional: true }
  },

  // FeatureRowItem (cms.feature-rows Items)
  FeatureRowItem: {
    title: { type: 'string', category: 'content', label: 'Title' },
    description: { type: 'textarea', category: 'content', label: 'Description', optional: true },
    image: { type: 'image', category: 'content', label: 'Image', optional: true },
    icon: { type: 'string', category: 'appearance', label: 'Icon', optional: true },
    ctaText: { type: 'string', category: 'content', label: 'Button Text', optional: true },
    ctaLink: { type: 'url', category: 'content', label: 'Button Link', optional: true },
    imageSide: {
      type: 'select',
      category: 'layout',
      label: 'Image Side',
      options: ['auto', 'left', 'right'],
      optional: true
    }
  }
}

/**
 * Get array item schema for a specific array field
 *
 * Priority:
 * 1. Manual mappings and schemas
 * 2. Generated mappings and schemas
 */
export function getArrayItemSchema(componentType: string, fieldName: string): Record<string, PropertyDefinition> {
  // Manual mappings take priority
  const manualMappings: Record<string, Record<string, string>> = {
    'cms.accordion-section': { Items: 'AccordionItem' },
    'cms.accordion-section-v2': { Items: 'AccordionItem' },
    'cms.accordion-v3': { Items: 'AccordionItem' },
    'cms.flex-section': { Items: 'FlexItem' },
    'cms.image-gallery': { images: 'ImageGalleryItem' },
    'cms.gallery-nav': { filters: 'GalleryNavFilter' },
    'cms.layout-grid': { Items: 'LayoutGridItem' },
    'cms.process-steps': { Items: 'ProcessStepItem' },
    'cms.location-block': { hours: 'OpeningHour' },
    'cms.logo-strip': { logos: 'LogoItem' },
    'cms.pricing': { Items: 'PricingTier' },
    'cms.menu-list': { categories: 'MenuCategory' },
    'cms.feature-rows': { Items: 'FeatureRowItem' }
  }

  // Try manual mapping first
  let componentMapping = manualMappings[componentType]
  let schemaKey = componentMapping?.[fieldName]

  // Fall back to generated mappings
  if (!schemaKey && generatedArrayMappings[componentType]) {
    schemaKey = generatedArrayMappings[componentType][fieldName]
  }

  if (!schemaKey) return {}

  return getArrayItemSchemaByName(schemaKey)
}

/**
 * Resolve an item schema by its `arrayItemSchemas` key (manual first, then generated).
 * Used for nested arrays inside array items (PropertyDefinition.itemSchema).
 */
export function getArrayItemSchemaByName(schemaKey: string): Record<string, PropertyDefinition> {
  return arrayItemSchemas[schemaKey] || generatedArrayItemSchemas[schemaKey] || {}
}

/**
 * Get all component types that have schemas defined
 * Combines manual and generated schemas
 */
export function getAvailableComponentTypes(): string[] {
  const manualTypes = Object.keys(componentSchemas)
  const generatedTypes = Object.keys(generatedComponentSchemas)

  // Combine and deduplicate
  return [...new Set([...manualTypes, ...generatedTypes])]
}